import { useLayoutEffect, useRef } from 'react'

import { Input } from '@/components/ui/input'

// Keeps digits and a single dot, with at most two decimal places and no
// leading zeros, so the raw value stays parseable by the form schema and only
// the display is formatted.
function cleanPrice(raw: string): string {
  let s = raw.replace(/[^0-9.]/g, '')
  const firstDot = s.indexOf('.')
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '')
  }
  const dot = s.indexOf('.')
  if (dot !== -1) s = s.slice(0, dot + 1) + s.slice(dot + 1, dot + 3)
  return s.replace(/^0+(?=\d)/, '')
}

function groupDigits(intPart: string): string {
  return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

function formatPrice(cleaned: string): string {
  const dot = cleaned.indexOf('.')
  const intPart = dot === -1 ? cleaned : cleaned.slice(0, dot)
  const frac = dot === -1 ? '' : cleaned.slice(dot)
  if (intPart === '') return frac // keep ".", ".5" typeable
  return groupDigits(intPart) + frac
}

// FormControl injects id/aria-* through the Slot, so the rest of the input
// props must flow through to the underlying element.
interface PriceInputProps extends Omit<
  React.ComponentProps<typeof Input>,
  'value' | 'onChange' | 'onBlur' | 'type'
> {
  value: unknown
  onChange: (value: string) => void
  onBlur: () => void
}

// Price field that inserts thousands separators as the user types. The form
// stores the comma-free string ("12345.5") and the display is derived from it
// on every render, so the two can never drift apart.
export function PriceInput({ value, onChange, onBlur, ...props }: PriceInputProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  // Digits left of the caret in the raw input, restored after reformatting so
  // editing mid-number doesn't jump the caret to the end.
  const caretDigits = useRef<number | null>(null)
  const display = formatPrice(cleanPrice(String(value ?? '')))

  useLayoutEffect(() => {
    const el = inputRef.current
    const target = caretDigits.current
    caretDigits.current = null
    if (!el || target == null || document.activeElement !== el) return
    let pos = 0
    let seen = 0
    while (pos < el.value.length && seen < target) {
      if (/[0-9]/.test(el.value[pos])) seen++
      pos++
    }
    el.setSelectionRange(pos, pos)
  }, [display])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    const caret = e.target.selectionStart ?? raw.length
    caretDigits.current = raw.slice(0, caret).replace(/[^0-9]/g, '').length
    onChange(cleanPrice(raw))
  }

  return (
    <Input
      {...props}
      ref={inputRef}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      value={display}
      onChange={handleChange}
      onBlur={onBlur}
    />
  )
}
